/**
 * Sidecar REST API (API_CONTRACT §5, §6). Call when operator answers or ends a call.
 */

import { resolveSidecarApiUrl } from "~/services/calling/config";
import type { SuperviseMode } from "~/services/calling/types";

export type CallAnsweredRequest = { extension: string; uniqueId: string };
export type CallEndedRequest = { extension: string; uniqueId: string };
export type QueueClaimRequest = { extension: string; uniqueId: string };
export type CallResponse = { success: boolean; activeCalls: number };

export type SuperviseRequest = {
  supervisorExtension: string;
  targetExtension: string;
  uniqueId: string;
  mode: SuperviseMode;
};

export type CallTransferRequest = {
  fromExtension: string;
  toExtension: string;
  uniqueId: string;
};
export type ProvisionedExtension = {
  extension: string;
  name?: string | null;
};

async function post(
  path: string,
  body: CallAnsweredRequest | CallEndedRequest | QueueClaimRequest
): Promise<CallResponse> {
  const base = resolveSidecarApiUrl();
  if (!base) throw new Error("Sidecar API URL not configured.");
  const url = `${base.replace(/\/$/, "")}${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as CallResponse | { error?: string };
  if (!res.ok) {
    throw new Error(
      (data && "error" in data && typeof data.error === "string"
        ? data.error
        : `Sidecar API ${path} failed: ${res.status}`)
    );
  }
  return data as CallResponse;
}

/** POST /call-answered. Call when the operator answers an incoming call. */
export async function callAnswered(
  extension: string,
  uniqueId: string
): Promise<CallResponse> {
  return post("/call-answered", { extension, uniqueId });
}

/** POST /call-ended. Call when the call ends (hangup). */
export async function callEnded(
  extension: string,
  uniqueId: string
): Promise<CallResponse> {
  return post("/call-ended", { extension, uniqueId });
}

/** POST /queue/claim. Reserve a ringing call before the SIP INVITE is delivered. */
export async function queueClaim(
  extension: string,
  uniqueId: string
): Promise<CallResponse> {
  return post("/queue/claim", { extension, uniqueId });
}

/** POST /call-transfer. Reassign an answered call to another operator after SIP REFER. */
export async function callTransfer(request: CallTransferRequest): Promise<void> {
  const base = resolveSidecarApiUrl();
  if (!base) throw new Error("Sidecar API URL not configured.");

  const url = `${base.replace(/\/$/, "")}/call-transfer`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  const data = (await res.json().catch(() => ({}))) as
    | { ok?: boolean; error?: string }
    | Record<string, unknown>;

  if (!res.ok) {
    throw new Error(
      typeof data === "object" && data && "error" in data && typeof data.error === "string"
        ? data.error
        : `Call transfer failed: ${res.status}`
    );
  }
}

/** POST /supervise. Start a listen or barge session on another operator's call. */
export async function supervise(request: SuperviseRequest): Promise<void> {
  const base = resolveSidecarApiUrl();
  if (!base) throw new Error("Sidecar API URL not configured.");

  const url = `${base.replace(/\/$/, "")}/supervise`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  const data = (await res.json().catch(() => ({}))) as
    | { ok?: boolean; error?: string }
    | Record<string, unknown>;

  if (!res.ok) {
    throw new Error(
      typeof data === "object" && data && "error" in data && typeof data.error === "string"
        ? data.error
        : `Supervise failed: ${res.status}`
    );
  }
}

type ExtensionsResponse = {
  success?: boolean;
  extensions?: Array<{
    extension?: string | null;
    name?: string | null;
  }>;
};

/**
 * GET /extensions.
 * Returns provisioned SIP extensions known by the sidecar/Asterisk node.
 */
export async function listProvisionedExtensions(): Promise<ProvisionedExtension[]> {
  const base = resolveSidecarApiUrl();
  if (!base) return [];

  const url = `${base.replace(/\/$/, "")}/extensions`;
  const res = await fetch(url, { method: "GET" });
  const data = (await res
    .json()
    .catch(() => ({}))) as ExtensionsResponse | { error?: string };

  if (!res.ok) {
    throw new Error(
      (data && "error" in data && typeof data.error === "string"
        ? data.error
        : `Sidecar API /extensions failed: ${res.status}`)
    );
  }

  const extensionItems = (Array.isArray((data as ExtensionsResponse).extensions)
    ? (data as ExtensionsResponse).extensions
    : []) as NonNullable<ExtensionsResponse["extensions"]>;

  return extensionItems
    .map((item) => ({
      extension: String(item.extension ?? "").trim(),
      name: typeof item.name === "string" ? item.name : null,
    }))
    .filter((item) => Boolean(item.extension));
}
